import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, coverUri } from "../lib/api";
import { initials } from "../lib/format";
import { Spinner, EmptyState, Chip } from "./ui";

// Only fields allowed by `unavailability.view_status`: name, voice, photo, convoked, availability, response.
const RESPONSE = { confirmed: ["CONFIRMOU", "var(--on-success)"], declined: ["NÃO VAI", "var(--warning)"], no_response: ["SEM RESPOSTA", "var(--muted)"] };
const AVAIL = { available: "Disponível", unavailable: "Indisponível", unknown: "Disponibilidade desconhecida" };
const FILTERS = [["all", "TODOS"], ["no_response", "SEM RESPOSTA"], ["declined", "NÃO VÃO"], ["unavailable", "INDISPONÍVEIS"]];

function Stat({ label, value, testid }) {
  return (
    <div className="card" style={{ padding: 12, textAlign: "center", flex: "1 1 90px" }} data-testid={testid}>
      <div style={{ fontFamily: "var(--f-medium)", fontSize: 22 }}>{value}</div>
      <div className="t-hint" style={{ fontSize: 11 }}>{label}</div>
    </div>
  );
}

function Row({ m }) {
  const [label, color] = RESPONSE[m.response] || RESPONSE.no_response;
  return (
    <div className="card" style={{ display: "flex", alignItems: "center", gap: 10, padding: 10, opacity: m.convoked ? 1 : 0.6 }} data-testid={`availability-row-${m.id}`}>
      <div className="avatar" style={{ width: 36, height: 36, fontSize: 12 }}>
        {coverUri(m.photo_url) ? <img src={coverUri(m.photo_url)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(m.name)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "var(--f-medium)", fontSize: 14 }}>{m.name}</div>
        <div className="t-hint" style={{ fontSize: 12 }}>{[m.voice, AVAIL[m.availability]].filter(Boolean).join(" · ")}</div>
      </div>
      {m.convoked ? <span className="pill" style={{ color, borderColor: color }} data-testid={`availability-response-${m.id}`}>{label}</span> : <span className="pill" data-testid={`availability-not-convoked-${m.id}`}>NÃO CONVOCADO</span>}
    </div>
  );
}

export function EventAvailability({ eventId }) {
  const [filter, setFilter] = useState("all");
  const q = useQuery({ queryKey: ["event-availability", eventId], queryFn: () => api(`/events/${eventId}/availability`), enabled: !!eventId });
  if (q.isLoading) return <Spinner />;
  if (q.isError) return <EmptyState icon="alert-circle-outline" text={q.error.message} data-testid="availability-error" />;
  const { summary = {}, members = [] } = q.data || {};
  const shown = members.filter((m) => (filter === "all" ? true : filter === "unavailable" ? m.availability === "unavailable" : m.convoked && m.response === filter));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }} data-testid="event-availability">
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Stat label="CONVOCADOS" value={summary.convoked ?? 0} testid="availability-convoked" />
        <Stat label="CONFIRMARAM" value={summary.confirmed ?? 0} testid="availability-confirmed" />
        <Stat label="NÃO VÃO" value={summary.declined ?? 0} testid="availability-declined" />
        <Stat label="SEM RESPOSTA" value={summary.no_response ?? 0} testid="availability-no-response" />
        <Stat label="INDISPONÍVEIS" value={summary.unavailable ?? 0} testid="availability-unavailable" />
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {FILTERS.map(([k, l]) => <Chip key={k} label={l} active={filter === k} onClick={() => setFilter(k)} data-testid={`availability-filter-${k}`} />)}
      </div>
      {!shown.length ? <EmptyState icon="people" text="Ninguém nesta situação." /> : null}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{shown.map((m) => <Row key={m.id} m={m} />)}</div>
      <div className="t-hint" style={{ fontSize: 12 }}>Os motivos das ausências e indisponibilidades são privados e não são mostrados aqui.</div>
    </div>
  );
}
